import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';

interface StandardsProgressionChartProps {
  dateRange: 'week' | 'month' | 'quarter' | 'year';
  districtId: string;
}

interface StandardProgressData {
  grade: string;
  notStarted: number;
  inProgress: number;
  completed: number;
  total: number;
}

const StandardsProgressionChart: React.FC<StandardsProgressionChartProps> = ({ dateRange, districtId }) => {
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<StandardProgressData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would call an API with the dateRange and districtId parameters
        // For now, we'll generate mock data based on the parameters
        const mockData = generateMockData(districtId);
        setProgressData(mockData);
      } catch (error) {
        console.error('Error fetching standards progression data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, districtId]);

  const generateMockData = (district: string): StandardProgressData[] => {
    const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];
    const result: StandardProgressData[] = [];
    
    // Generate data for each grade
    for (const grade of grades) {
      // Base values
      const baseTotal = district === 'all' ? 500 : 150;
      const baseCompleted = Math.round(baseTotal * (0.3 + Math.random() * 0.4)); // 30-70% completion
      const baseInProgress = Math.round(baseTotal * (0.2 + Math.random() * 0.3)); // 20-50% in progress
      const baseNotStarted = baseTotal - baseCompleted - baseInProgress;
      
      // Adjust based on grade (lower grades have higher completion rates)
      const gradeIndex = grade === 'K' ? 0 : parseInt(grade);
      const gradeFactor = 1 - (gradeIndex / 10); // Decreases as grade increases
      
      const total = Math.round(baseTotal * (0.8 + gradeFactor * 0.4));
      const completed = Math.round(baseCompleted * gradeFactor * 1.5);
      const inProgress = Math.round(baseInProgress * (1 + (1 - gradeFactor) * 0.5));
      const notStarted = total - completed - inProgress;
      
      result.push({
        grade,
        notStarted,
        inProgress,
        completed,
        total
      });
    }
    
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate totals for summary
  const totalStandards = progressData.reduce((sum, data) => sum + data.total, 0);
  const totalCompleted = progressData.reduce((sum, data) => sum + data.completed, 0);
  const totalInProgress = progressData.reduce((sum, data) => sum + data.inProgress, 0);
  const totalNotStarted = progressData.reduce((sum, data) => sum + data.notStarted, 0);
  
  // Calculate percentages for summary
  const completedPercentage = Math.round((totalCompleted / totalStandards) * 100);
  const inProgressPercentage = Math.round((totalInProgress / totalStandards) * 100);
  const notStartedPercentage = Math.round((totalNotStarted / totalStandards) * 100);

  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">Completed</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">In Progress</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-gray-300 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">Not Started</span>
        </div>
      </div>
      
      <div className="space-y-3">
        {progressData.map((data) => (
          <div key={data.grade} className="flex items-center">
            <div className="w-8 text-sm font-medium text-gray-700 mr-2">
              {data.grade === 'K' ? 'K' : `G${data.grade}`}
            </div>
            <div className="flex-1 h-6 bg-gray-200 rounded-full overflow-hidden">
              <div className="flex h-full">
                <div 
                  className="bg-green-500 h-full"
                  style={{ width: `${(data.completed / data.total) * 100}%` }}
                  title={`Completed: ${data.completed} (${Math.round((data.completed / data.total) * 100)}%)`}
                ></div>
                <div 
                  className="bg-yellow-500 h-full"
                  style={{ width: `${(data.inProgress / data.total) * 100}%` }}
                  title={`In Progress: ${data.inProgress} (${Math.round((data.inProgress / data.total) * 100)}%)`}
                ></div>
                <div 
                  className="bg-gray-300 h-full"
                  style={{ width: `${(data.notStarted / data.total) * 100}%` }}
                  title={`Not Started: ${data.notStarted} (${Math.round((data.notStarted / data.total) * 100)}%)`}
                ></div>
              </div>
            </div>
            <div className="ml-2 text-xs text-gray-500 w-16 text-right">
              {data.total} total
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Overall Standards Progression</h3>
        <div className="flex items-center mb-2">
          <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
            <div className="flex h-full">
              <div 
                className="bg-green-500 h-full"
                style={{ width: `${completedPercentage}%` }}
              ></div>
              <div 
                className="bg-yellow-500 h-full"
                style={{ width: `${inProgressPercentage}%` }}
              ></div>
              <div 
                className="bg-gray-300 h-full"
                style={{ width: `${notStartedPercentage}%` }}
              ></div>
            </div>
          </div>
          <div className="ml-2 text-sm font-medium text-gray-700">
            {totalStandards} total
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div>
            <span className="text-green-700 font-medium">{completedPercentage}%</span> Completed
          </div>
          <div>
            <span className="text-yellow-700 font-medium">{inProgressPercentage}%</span> In Progress
          </div>
          <div>
            <span className="text-gray-700 font-medium">{notStartedPercentage}%</span> Not Started
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandardsProgressionChart;
